/**
 * CellBindingPicker.tsx — the table.row.column picker for param cell
 * bindings (ParamSpec.binding / GraphEdge.weightBinding).
 *
 * Rendered INLINE under a param row (or the edge weight row) — not a modal —
 * so the binding sits next to the value it drives. The flow mirrors the db
 * knob create form: table → key column → row → numeric column. The row is
 * addressed by its key-column value (not the row id), so the binding
 * survives re-seeds and project import/export.
 *
 * Also exports `BindingBadge` (the bound/unbound state chip shown on the row
 * itself) and the `BindingTools` prop bundle the modals thread down from App
 * (tables snapshot + lazy load + the PATCH seam for slider write-through).
 */
import { useEffect, useMemo, useState } from 'react';
import { LinkBreak, Link, Warning, X } from '@phosphor-icons/react';

import type { CellBinding } from '../engine/types';
import { bindingLabel, lookupBoundCell, type BindingTableSnapshot } from '../engine/bindings';

/** The DB access the binding UI needs — threaded from App (sim.tablesState /
 *  sim.loadTables / sim.updateTableCell). Absent in local mode: binding
 *  controls then render but resolve as broken (no snapshot to read). */
export interface BindingTools {
  tables: BindingTableSnapshot[] | null;
  onLoadTables?: () => void;
  onUpdateTableCell?: (table: string, rowId: number | string, column: string, value: number) => void;
}

const NUMERIC_TYPES = new Set(['integer', 'float', 'real', 'numeric', 'int', 'number']);
function isNumeric(type: string | undefined): boolean {
  return !!type && NUMERIC_TYPES.has(type.toLowerCase());
}

// ─── Badge ──────────────────────────────────────────────────────────────────

/** The on-row state chip for a binding: link icon + `table[key=val].col`,
 *  the resolved DB value (or a broken ⚠ badge), and an unbind button.
 *  Clicking the label opens the picker (the parent controls open state). */
export function BindingBadge({
  binding,
  tools,
  onOpen,
  onUnbind,
}: {
  binding: CellBinding;
  tools?: BindingTools;
  onOpen: () => void;
  onUnbind: () => void;
}): JSX.Element {
  const lookup = useMemo(
    () => (tools ? lookupBoundCell(tools.tables, binding) : null),
    [tools, binding],
  );
  const broken = !lookup || lookup.rowId === null || lookup.value === null;
  return (
    <span className="binding-badge">
      <button
        type="button"
        className={`binding-badge-chip${broken ? ' broken' : ''}`}
        onClick={onOpen}
        title={broken
          ? 'Binding broken — no matching row/cell; the saved default is in effect'
          : `Bound to ${bindingLabel(binding)} — the DB cell drives this value`}
      >
        {broken ? <Warning size={12} /> : <Link size={12} />}
        <code>{bindingLabel(binding)}</code>
        {lookup?.value != null && <span className="binding-badge-value">= {lookup.value}</span>}
      </button>
      <button
        type="button"
        className="input-ext-btn"
        title="Remove binding (keep the saved default)"
        onClick={onUnbind}
      >
        <LinkBreak size={13} />
      </button>
    </span>
  );
}

// ─── Picker ─────────────────────────────────────────────────────────────────

/**
 * The 4-step picker. Local draft state initialised from `value`; a COMPLETE
 * selection (table + key column + key + numeric column) commits through
 * `onChange` on every change — partial selections never clobber an existing
 * binding. `onChange(null)` only fires from the explicit Unbind button.
 */
export function CellBindingPicker({
  value,
  onChange,
  tools,
  onClose,
}: {
  value: CellBinding | null;
  onChange: (binding: CellBinding | null) => void;
  tools?: BindingTools;
  onClose: () => void;
}): JSX.Element {
  const tables = tools?.tables ?? null;
  // Pull the snapshot once when the picker opens (mirrors the db-knob form).
  useEffect(() => {
    if (tools?.onLoadTables && tables === null) tools.onLoadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleTables = useMemo(
    () => (tables ?? []).filter((t) => !t.name.startsWith('chat_') && !t.name.startsWith('dataset_')),
    [tables],
  );

  const [table, setTable] = useState(value?.table ?? visibleTables[0]?.name ?? '');
  const [keyColumn, setKeyColumn] = useState(value?.keyColumn ?? '');
  const [key, setKey] = useState(value?.key ?? '');
  const [column, setColumn] = useState(value?.column ?? '');
  const [rowQuery, setRowQuery] = useState('');

  const selectedTable = visibleTables.find((t) => t.name === table) ?? null;
  const textColumns = useMemo(
    () => (selectedTable?.columns ?? []).filter((c) => !isNumeric(c.type)),
    [selectedTable],
  );
  const numericColumns = useMemo(
    () => (selectedTable?.columns ?? []).filter((c) => isNumeric(c.type)),
    [selectedTable],
  );
  // Default the key column to the first text column when switching tables.
  useEffect(() => {
    setKeyColumn(textColumns[0]?.name ?? '');
    setKey('');
    setColumn('');
    setRowQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const effectiveKeyColumn = keyColumn || textColumns[0]?.name || '';
  const rowLabel = (r: { id: number | string; cells: Record<string, unknown> }): string => {
    const k = effectiveKeyColumn ? r.cells[effectiveKeyColumn] : undefined;
    const firstText = Object.entries(r.cells).find(([, v]) => typeof v === 'string')?.[1];
    const head = k !== undefined && k !== null && k !== '' ? String(k) : `#${r.id}`;
    return `${head}${firstText && String(firstText) !== String(k) ? ` · ${firstText}` : ''}`;
  };
  const filteredRows = useMemo(() => {
    if (!selectedTable) return [];
    const q = rowQuery.trim().toLowerCase();
    const rows = q
      ? selectedTable.rows.filter((r) => rowLabel(r).toLowerCase().includes(q) || String(r.id) === q)
      : selectedTable.rows;
    return rows.slice(0, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, rowQuery, effectiveKeyColumn]);

  const preview: CellBinding | null =
    table && effectiveKeyColumn && key && column
      ? { table, keyColumn: effectiveKeyColumn, key, column }
      : null;

  // Commit whenever the selection is complete (immediate-apply, the codebase's
  // modal convention). Partial selections hold the previous binding.
  useEffect(() => {
    if (preview) onChange(preview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.table, preview?.keyColumn, preview?.key, preview?.column]);

  if (tables === null) {
    return (
      <div className="binding-picker">
        <p className="knob-empty">
          {tools?.onLoadTables ? 'Loading project tables…' : 'No project tables (connect a backend to bind).'}
        </p>
      </div>
    );
  }
  if (visibleTables.length === 0) {
    return (
      <div className="binding-picker">
        <p className="knob-empty">This project has no tables. Create one in the Tables panel first.</p>
      </div>
    );
  }

  const lookup = preview ? lookupBoundCell(tables, preview) : null;

  return (
    <div className="binding-picker">
      <div className="knob-db-grid">
        <label className="knob-field">
          <span>Table</span>
          <select className="text-input" value={table} onChange={(e) => setTable(e.target.value)}>
            {visibleTables.map((t) => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </label>

        <label className="knob-field">
          <span>Key column</span>
          <select
            className="text-input"
            value={effectiveKeyColumn}
            onChange={(e) => {
              setKeyColumn(e.target.value);
              setKey('');
            }}
            disabled={!selectedTable}
            title="The column whose value identifies the row — the binding follows that value across re-seeds and imports"
          >
            {textColumns.length === 0 && <option value="">— none —</option>}
            {textColumns.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className="knob-field">
          <span>Value column <em className="knob-field-hint">(numeric)</em></span>
          <select
            className="text-input"
            value={column}
            onChange={(e) => setColumn(e.target.value)}
            disabled={!selectedTable}
          >
            <option value="">— select —</option>
            {numericColumns.map((c) => (
              <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
            ))}
          </select>
          {selectedTable && numericColumns.length === 0 && (
            <em className="knob-field-hint">This table has no numeric columns.</em>
          )}
        </label>

        <label className="knob-field knob-field-wide">
          <span>Search row</span>
          <input
            className="text-input"
            placeholder="Filter by key or text…"
            value={rowQuery}
            onChange={(e) => setRowQuery(e.target.value)}
            disabled={!selectedTable}
          />
        </label>

        <div className="knob-field knob-field-wide knob-row-picker">
          <span>Select a row</span>
          <ul className="knob-row-list">
            {filteredRows.map((r) => {
              const k = effectiveKeyColumn ? r.cells[effectiveKeyColumn] : undefined;
              const disabled = k === undefined || k === null || k === '';
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`knob-row-pick${disabled ? '' : key === String(k) ? ' on' : ''}`}
                    disabled={disabled}
                    title={disabled ? 'This row has no value in the key column' : undefined}
                    onClick={() => setKey(String(k))}
                  >
                    {rowLabel(r)}
                  </button>
                </li>
              );
            })}
            {filteredRows.length === 0 && <li className="knob-empty">No rows.</li>}
          </ul>
        </div>
      </div>

      {preview && (
        <p className={`binding-preview${lookup && lookup.value == null ? ' broken' : ''}`}>
          {lookup && lookup.value != null ? (
            <>Bound to <code>{bindingLabel(preview)}</code> — current value <strong>{lookup.value}</strong>.</>
          ) : (
            <>⚠ No matching row/cell for <code>{bindingLabel(preview)}</code> — the saved default stays in effect until the cell exists.</>
          )}
        </p>
      )}

      <div className="binding-picker-actions">
        <button type="button" className="btn btn-mini" onClick={() => onChange(null)}>
          <LinkBreak size={13} /> Unbind
        </button>
        <button type="button" className="btn btn-mini" onClick={onClose}>
          <X size={13} /> Close
        </button>
      </div>
    </div>
  );
}
